import {
  ActionIcon,
  Alert,
  Badge,
  Box,
  Button,
  Card,
  Divider,
  Group,
  Loader,
  Modal,
  Stack,
  Text,
  ThemeIcon,
  Title,
  Tooltip,
} from "@mantine/core"
import { useTranslation } from "react-i18next"
import { useDisclosure } from "@mantine/hooks"
import { DragDropContext, Draggable, Droppable, type DropResult } from "@hello-pangea/dnd"
import { useEffect, type ReactNode } from "react"
import {
  CalendarBlankIcon,
  ClockIcon,
  DotsSixVerticalIcon,
  MapPinIcon,
  MapTrifoldIcon,
  PackageIcon,
  ScalesIcon,
  SparkleIcon,
  TruckIcon,
  UserIcon,
  UsersThreeIcon,
} from "@phosphor-icons/react"
import dayjs from "dayjs"
import useSWR from "swr"
import { DeliveryRouteApi, MetricsApi } from "../../apis"
import { IRouteStop } from "../../apis/delivery-route"
import { message } from "../../utils/message"
import { renderFullName } from "../../utils/render-name"
import { DisabledReason, HelpButtonGroup, useHelpContext } from "@dehub/theme"
import { DeliveryActionButton } from "../delivery/action-controls"
import { useDeliveryActions } from "../delivery/delivery-actions"
import { usePermission } from "../permission"
import { RouteStatusBadge, StopStatusBadge } from "./atoms"
import { useRouteQuery, useRouteMutations } from "./hooks"
import { RouteMap } from "./RouteMap"
import { translateRouteError } from "./route-errors"
import { useVehicleClassLabel } from "./vehicle-class"
import { StatusPill } from "../ui/status-pill"

// Ulaanbaatar city centre: only the last-resort optimization origin when the route's warehouse has no coordinates.
const UB_LAT = 47.9184
const UB_LNG = 106.9177

// A stop counts as closed once its delivery note can no longer change (the same set the BE complete gate uses).
const CLOSED_DN_STATUSES = ["DELIVERED", "CANCELLED", "RETURNED"]

interface Props {
  routeId: string
}

// One labelled meta line in the header (icon + label + value), e.g. the
// driver, the vehicle, the planned date.
function MetaItem({
  icon,
  label,
  value,
  color = "gray",
}: {
  icon: ReactNode
  label: string
  value: ReactNode
  color?: string
}) {
  return (
    <Group gap={8} wrap="nowrap" align="center">
      <ThemeIcon variant="light" color={color} size={32} radius="md">
        {icon}
      </ThemeIcon>
      <Box miw={0}>
        <Text size="sm" c="gray.7" lh={1.2}>
          {label}
        </Text>
        <Text size="sm" fw={600} lh={1.3} lineClamp={2}>
          {value}
        </Text>
      </Box>
    </Group>
  )
}

// One compact progress/planning counter.
function StatBox({ label, value, color }: { label: string; value: ReactNode; color?: string }) {
  return (
    <Box>
      <Text size="sm" c="gray.7">{label}</Text>
      <Text fw={700} size="lg" c={color}>{value}</Text>
    </Box>
  )
}

const driverFullName = (d: { lastName: string | null; firstName: string | null } | null | undefined): string => {
  if (!d) return "-"
  return renderFullName(d)
}

export function DeliveryRouteDetail({ routeId }: Props) {
  const { t } = useTranslation(["delivery", "common"])
  const { data: route, isLoading, error } = useRouteQuery(routeId)
  const { start, loadingComplete, complete, optimize, reorderStops, submitting } = useRouteMutations()
  const [optimizeOpened, { open: openOptimize, close: closeOptimize }] = useDisclosure(false)
  const [completeOpened, { open: openComplete, close: closeComplete }] = useDisclosure(false)
  const { stateOf } = useDeliveryActions()
  const classLabel = useVehicleClassLabel()

  const ORD_DN_MNG = usePermission("ORD_DN_MNG")
  const isRouteInProgress = route?.status === "IN_PROGRESS"
  const isRouteDone = route?.status === "COMPLETED"
  const routeDriverId = route?.driverId ?? null

  // §2.5 Tab 4 — GPS trail playback. Managers (ORD_DN_MNG) get the full driver
  // trail for DONE or in-flight routes; poll while IN_PROGRESS so the trail
  // grows live. The trail supersedes the live-pucks feed (scrub-to-end = "now"),
  // so we only fetch the lighter live feed when we're NOT loading the trail.
  const wantTrail = ORD_DN_MNG.hasAccess && (isRouteDone || isRouteInProgress)
  const { data: trailData, isLoading: trailLoading } = useSWR(
    wantTrail ? ["route-loc-history", routeId] : null,
    () => DeliveryRouteApi.getLocationHistory(routeId),
    { revalidateOnFocus: false, refreshInterval: isRouteInProgress ? 30000 : 0 },
  )
  const trail = trailData?.rows ?? []

  // Live driver marker (non-manager view, or when the trail isn't loaded). The
  // metrics feed returns the latest GPS point per IN_PROGRESS DN for the
  // business; poll (30s) and filter to this route's driver.
  const { data: driverLoc } = useSWR(
    !wantTrail && isRouteInProgress && routeDriverId ? ["route-driver-loc", routeId, routeDriverId] : null,
    () => MetricsApi.driverLocations({ freshMinutes: 15 }),
    { refreshInterval: 30000, revalidateOnFocus: false },
  )

  // @dehub/help: the route status drives the drawer's «Одоо» step (R15 flow) and the status tab's current marker.
  const { set: setHelpContext } = useHelpContext()
  useEffect(() => {
    setHelpContext({ status: route?.status ?? undefined })
  }, [route?.status, setHelpContext])

  if (isLoading) {
    return (
      <Box p="xl" ta="center">
        <Loader size="sm" />
      </Box>
    )
  }

  if (error || !route) {
    return (
      <Alert color="gray" variant="light">
        {t("delivery:route.detail.loadError")}
      </Alert>
    )
  }

  const isTerminal = route.status === "COMPLETED" || route.status === "CANCELLED"
  // Progress counters (Дууссан/Амжилтгүй/Алгассан) are only meaningful once the
  // route has left; before that they're all 0 and just add noise.
  const showProgress = isRouteInProgress || isTerminal

  const optimizeState = stateOf("routeOptimize", route)
  const loadingState = stateOf("routeLoadingComplete", route)
  const startState = stateOf("routeStart", route)
  const completeState = stateOf("routeComplete", route)
  // @dehub/help reason code: permission reasons mention «эрх»; otherwise the status/data reason the caller names.
  const helpReason = (st: { allowed: boolean; reason: string | null }, fallback: string) => (st.allowed ? null : /эрх|permission/i.test(st.reason ?? "") ? "PERM" : fallback)
  const reorderState = stateOf("routeReorder", route)

  // Ачилтын (бэлдэх) ажилтан — assigned post-create, may be one or many.
  const loadingStaff = (route.pullSheetStaffs?.length
    ? route.pullSheetStaffs
    : route.pullSheetStaff
      ? [route.pullSheetStaff]
      : []
  )
    .map((s) => (s.lastName || s.firstName ? renderFullName(s) : s.email || ""))
    .filter(Boolean)

  // Бүс / чиглэл — derived from the route's stops (de_network distribution areas).
  const zones = route.zones ?? []

  const stops: IRouteStop[] = route.stops ?? []
  const closedStops = stops.filter((s) => CLOSED_DN_STATUSES.includes(s.dn?.deliveryNoteStatus ?? "")).length

  // Optimization starts and ends at the route's warehouse; only a warehouse without coordinates falls back to the city centre (O120).
  const wh = route.warehouse
  const whLat = wh?.latitude != null ? Number(wh.latitude) : NaN
  const whLng = wh?.longitude != null ? Number(wh.longitude) : NaN
  const hasWarehouseCoords = Number.isFinite(whLat) && Number.isFinite(whLng)
  const origin = hasWarehouseCoords ? { lat: whLat, lng: whLng } : { lat: UB_LAT, lng: UB_LNG }

  // Several stops on one coordinate: the map shows a single pin, tell the dispatcher (O123).
  const samePoint = (() => {
    const counts = new Map<string, number>()
    for (const s of stops) {
      if (s.latitude == null || s.longitude == null) continue
      const key = `${Number(s.latitude).toFixed(6)}, ${Number(s.longitude).toFixed(6)}`
      counts.set(key, (counts.get(key) ?? 0) + 1)
    }
    let best: { key: string; count: number } | null = null
    counts.forEach((count, key) => {
      if (count > 1 && (!best || count > best.count)) best = { key, count }
    })
    return best as { key: string; count: number } | null
  })()

  const run = async (fn: () => Promise<unknown>, okKey: string) => {
    try {
      await fn()
      message.success(t(okKey))
      return true
    } catch (e) {
      message.error(translateRouteError(e))
      return false
    }
  }

  const handleStart = () => run(() => start(route.id), "delivery:route.detail.started")
  const handleLoadingComplete = () => run(() => loadingComplete(route.id), "delivery:route.detail.loadingCompleted")
  const handleComplete = async () => {
    // Confirmed in the dialog; a BE refusal (stops still open) is shown translated, never raw English.
    if (await run(() => complete(route.id), "delivery:route.detail.completed")) closeComplete()
  }
  const handleOptimize = async () => {
    const ok = await run(
      () => optimize(route.id, { startFrom: origin, returnTo: origin }),
      "delivery:route.detail.optimizedDone",
    )
    if (ok) closeOptimize()
  }

  // Only this route's driver's live point(s); empty unless IN_PROGRESS.
  const driverPucks = (driverLoc?.rows ?? []).filter((r) => r.driverUserId === routeDriverId)
  // Reordering by drag; a route that has left, or without permission, locks the order.
  const canReorder = reorderState.applies && reorderState.allowed && stops.length > 1
  const onDragEnd = async (result: DropResult) => {
    if (!result.destination) return
    const from = result.source.index
    const to = result.destination.index
    if (from === to) return
    const next = [...stops]
    const [moved] = next.splice(from, 1)
    next.splice(to, 0, moved)
    try {
      await reorderStops(route.id, next.map((s) => s.id))
    } catch (e) {
      message.error(translateRouteError(e))
    }
  }

  const unassigned = (
    <Text span c="gray.7" fw={400}>
      {t("delivery:route.card.unassigned")}
    </Text>
  )

  return (
    <Stack gap="md">
      {/* Header card */}
      <Card withBorder radius="lg" p="md">
        {/* Identity + workflow actions */}
        <Group justify="space-between" align="flex-start" wrap="wrap" mb="md">
          <Box miw={0}>
            <Group gap="sm" mb={2} align="center">
              <Title order={3} lineClamp={1}>{route.name ?? route.routeNumber}</Title>
              <RouteStatusBadge status={route.status} size="lg" />
              {route.isOptimized && (
                <StatusPill tone="green">{t("delivery:route.detail.optimized")}</StatusPill>
              )}
            </Group>
            {route.name && (
              <Text size="sm" c="gray.7" ff="monospace">
                {route.routeNumber}
              </Text>
            )}
          </Box>
          <Group gap="xs" wrap="wrap">
            {optimizeState.applies && (
              <DeliveryActionButton
                state={optimizeState}
                variant="default"
                leftSection={<SparkleIcon size={18} />}
                disabled={submitting}
                onClick={openOptimize}
              >
                {t("delivery:route.detail.optimize")}
              </DeliveryActionButton>
            )}
            {/* State-changing route actions: attached «?» (R2) + reason popover while unavailable (R4, @dehub/help). */}
            {loadingState.applies && route.status === "PLANNED" && (
              <DisabledReason helpKey="order.delivery-routes-detail.reason.route-action" reason={helpReason(loadingState, "NOT_PLANNING")} when={!loadingState.allowed} position="bottom-end">
                <HelpButtonGroup helpKey="order.delivery-routes-detail.action.route-loading-complete">
                  <DeliveryActionButton state={loadingState} variant="default" loading={submitting} onClick={handleLoadingComplete}>
                    {t("delivery:route.detail.loadingComplete")}
                  </DeliveryActionButton>
                </HelpButtonGroup>
              </DisabledReason>
            )}
            {startState.applies && (
              <DisabledReason helpKey="order.delivery-routes-detail.reason.route-action" reason={helpReason(startState, "NOT_LOADED")} when={!startState.allowed} position="bottom-end">
                <HelpButtonGroup helpKey="order.delivery-routes-detail.action.route-start">
              <DeliveryActionButton data-help-id="action-route-start" state={startState} loading={submitting} onClick={handleStart}>
                {t("delivery:route.detail.start")}
              </DeliveryActionButton>
                </HelpButtonGroup>
              </DisabledReason>
            )}
            {completeState.applies && (
              <DisabledReason helpKey="order.delivery-routes-detail.reason.route-action" reason={helpReason(completeState, "NOT_PLANNING")} when={!completeState.allowed} position="bottom-end">
                <HelpButtonGroup helpKey="order.delivery-routes-detail.action.route-complete">
                  <DeliveryActionButton state={completeState} loading={submitting} onClick={openComplete}>
                    {t("delivery:route.detail.complete")}
                  </DeliveryActionButton>
                </HelpButtonGroup>
              </DisabledReason>
            )}
          </Group>
        </Group>

        {/* Who & when — the things an operator scans for first */}
        <Group gap="xl" wrap="wrap">
          <MetaItem
            icon={<UserIcon size={18} />}
            color="gray"
            label={t("delivery:route.card.driver")}
            value={
              route.driver ? (
                <>
                  {driverFullName(route.driver)}
                  {route.driver.phone && (
                    <Text span size="sm" c="gray.7" ml={6}>
                      {route.driver.phone}
                    </Text>
                  )}
                </>
              ) : (
                unassigned
              )
            }
          />
          <MetaItem
            icon={<TruckIcon size={18} />}
            color="gray"
            label={t("delivery:route.detail.vehicle")}
            value={
              route.vehicle ? `${route.vehicle.plateNumber} · ${classLabel(route.vehicle.vehicleClass)}` : unassigned
            }
          />
          <MetaItem
            icon={<UsersThreeIcon size={18} />}
            color="gray"
            label={t("delivery:route.detail.loader")}
            value={loadingStaff.length > 0 ? loadingStaff.join(", ") : unassigned}
          />
          <MetaItem
            icon={<MapTrifoldIcon size={18} />}
            color="gray"
            label={t("delivery:route.detail.zone")}
            value={
              zones.length > 0
                ? zones.join(", ")
                : <Text span c="gray.7" fw={400}>{t("delivery:route.detail.zoneUnknown")}</Text>
            }
          />
          <MetaItem
            icon={<CalendarBlankIcon size={18} />}
            color="gray"
            label={t("delivery:route.detail.plannedDate")}
            value={route.plannedDate ? dayjs(route.plannedDate).format("YYYY-MM-DD") : "-"}
          />
          {route.plannedStartTime && (
            <MetaItem
              icon={<ClockIcon size={18} />}
              color="gray"
              label={t("delivery:route.detail.startTime")}
              value={dayjs(route.plannedStartTime).format("HH:mm")}
            />
          )}
        </Group>

        {route.notes && (
          <Box mt="md">
            <Text size="sm" c="gray.7">{t("delivery:route.detail.notes")}</Text>
            <Text size="sm" style={{ whiteSpace: "pre-wrap" }}>{route.notes}</Text>
          </Box>
        )}

        <Divider my="md" />

        {/* Once the route is underway, show delivery progress; before that the
            0/0/0 counters are noise, so show the planning summary instead. */}
        {showProgress ? (
          <Group gap="xl">
            <StatBox label={t("delivery:route.detail.totalStops")} value={route.totalStops} />
            <StatBox label={t("delivery:route.detail.completedStops")} value={route.completedStops} color="green" />
            <StatBox label={t("delivery:route.detail.failedStops")} value={route.failedStops} />
            <StatBox label={t("delivery:route.detail.skippedStops")} value={route.skippedStops} />
            {route.totalDistanceKm && (
              <StatBox label={t("delivery:route.card.distance")} value={`${route.totalDistanceKm} ${t("delivery:route.unit.km")}`} />
            )}
            {route.plannedDurationMinutes != null && (
              <StatBox label={t("delivery:route.card.duration")} value={`${route.plannedDurationMinutes} ${t("delivery:route.unit.min")}`} />
            )}
          </Group>
        ) : (
          <Group gap="xl" wrap="wrap">
            <StatBox label={t("delivery:route.detail.totalStops")} value={route.totalStops} />
            {route.totalDistanceKm && (
              <StatBox label={t("delivery:route.detail.totalDistance")} value={`${route.totalDistanceKm} ${t("delivery:route.unit.km")}`} />
            )}
            {route.plannedDurationMinutes != null && (
              <StatBox label={t("delivery:route.detail.totalDuration")} value={`${route.plannedDurationMinutes} ${t("delivery:route.unit.min")}`} />
            )}
          </Group>
        )}
      </Card>

      {/* Spec §2.5 Tab 2 map + Tab 4 live driver / GPS trail playback */}
      {stops.length > 0 && (
        <Card withBorder radius="lg" p="md">
          <Group justify="space-between" mb="xs" wrap="wrap">
            <Text fw={700} size="md">{t("delivery:route.detail.map")}</Text>
            {wh?.name && (
              <Text size="sm" c="gray.7">{t("delivery:route.detail.warehouse", { name: wh.name })}</Text>
            )}
          </Group>
          {samePoint && (
            <Alert color="gray" variant="light" mb="xs" icon={<MapPinIcon size={18} />}>
              {t("delivery:route.detail.samePoint", { count: samePoint.count, point: samePoint.key })}
            </Alert>
          )}
          <RouteMap stops={stops} height={320} driverPucks={driverPucks} trail={trail} showWarehouse warehouse={wh} />
        </Card>
      )}
      {wantTrail && !trailLoading && trail.length === 0 && (
        <Alert color="gray" variant="light" icon={<MapPinIcon size={18} />}>
          {t("delivery:route.detail.noTrail")}
        </Alert>
      )}

      {/* Stop sequence */}
      <Box>
        <Group gap="xs" mb="xs" align="center">
          <Text fw={700} size="md">{t("delivery:route.detail.sequence")}</Text>
          <Badge size="lg" variant="light" color="gray" radius="sm">
            {stops.length}
          </Badge>
        </Group>
        {stops.length === 0 ? (
          <Text size="sm" c="gray.7">
            {t("delivery:route.detail.noStops")}
          </Text>
        ) : (
          <DragDropContext onDragEnd={onDragEnd}>
            <Droppable droppableId="route-stops">
              {(dropProvided) => (
                <Stack gap="xs" ref={dropProvided.innerRef} {...dropProvided.droppableProps}>
                  {stops.map((stop, idx) => {
                    const dn = stop.dn ?? null
                    const title =
                      dn?.receiverBusinessName ??
                      dn?.receiverBranchName ??
                      stop.address ??
                      t("delivery:route.detail.stopN", { n: stop.stopNumber })
                    // Business and branch often carry the same name: show it once (O123).
                    const branchUnderTitle =
                      dn?.receiverBusinessName && dn?.receiverBranchName && dn.receiverBranchName !== dn.receiverBusinessName
                        ? dn.receiverBranchName
                        : null
                    return (
                      <Draggable
                        key={stop.id}
                        draggableId={stop.id}
                        index={idx}
                        isDragDisabled={!canReorder || submitting}
                      >
                        {(dragProvided, dragSnapshot) => (
                          <Card
                            withBorder
                            radius="md"
                            p="sm"
                            ref={dragProvided.innerRef}
                            {...dragProvided.draggableProps}
                            style={{
                              ...dragProvided.draggableProps.style,
                              boxShadow: dragSnapshot.isDragging ? "var(--mantine-shadow-md)" : undefined,
                            }}
                          >
                            <Group justify="space-between" wrap="nowrap" gap="sm" align="flex-start">
                              <Group
                                gap="sm"
                                wrap="nowrap"
                                align="flex-start"
                                style={{ minWidth: 0, flex: 1 }}
                              >
                                <Tooltip
                                  label={reorderState.allowed || !reorderState.applies ? t("delivery:route.detail.drag") : reorderState.reason}
                                  withArrow
                                >
                                  <ActionIcon
                                    variant="light"
                                    color="gray"
                                    size="lg"
                                    style={{ cursor: canReorder ? "grab" : "not-allowed", flexShrink: 0 }}
                                    aria-label={t("delivery:route.detail.drag")}
                                    aria-disabled={!canReorder}
                                    {...dragProvided.dragHandleProps}
                                  >
                                    <DotsSixVerticalIcon size={18} />
                                  </ActionIcon>
                                </Tooltip>
                                <Box
                                  w={32}
                                  h={32}
                                  bg="gray.1"
                                  c="gray.9"
                                  style={{
                                    borderRadius: 8,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    fontWeight: 700,
                                    fontSize: 14,
                                    flexShrink: 0,
                                  }}
                                >
                                  {stop.stopNumber}
                                </Box>
                                <Box style={{ minWidth: 0 }}>
                                  <Text size="md" fw={600} lineClamp={1}>
                                    {title}
                                  </Text>
                                  {branchUnderTitle && (
                                    <Text size="sm" c="gray.7" lineClamp={1}>
                                      {branchUnderTitle}
                                    </Text>
                                  )}
                                  {/* Хаяг */}
                                  {stop.address && (
                                    <Group gap={4} wrap="nowrap" mt={2}>
                                      <MapPinIcon
                                        size={16}
                                        color="var(--mantine-color-gray-6)"
                                        style={{ flexShrink: 0 }}
                                      />
                                      <Text size="sm" c="gray.7" lineClamp={1}>
                                        {stop.address}
                                      </Text>
                                    </Group>
                                  )}
                                  <Group gap="md" wrap="nowrap" mt={4}>
                                    {stop.plannedArrivalTime && (
                                      <Group gap={4} wrap="nowrap">
                                        <ClockIcon size={16} color="var(--mantine-color-gray-6)" />
                                        <Text size="sm" c="gray.7">
                                          {dayjs(stop.plannedArrivalTime).format("HH:mm")}
                                        </Text>
                                      </Group>
                                    )}
                                    {dn?.refCode && (
                                      <Text size="sm" fw={700} ff="monospace">
                                        {dn.refCode}
                                      </Text>
                                    )}
                                  </Group>
                                </Box>
                              </Group>

                              <Group gap="xs" wrap="nowrap" align="center" style={{ flexShrink: 0 }}>
                                {/* Барааны хэмжээ */}
                                {dn?.sizeCode && (
                                  <Tooltip label={t("delivery:route.detail.size")} withArrow>
                                    <Badge
                                      size="lg"
                                      variant="light"
                                      color="gray"
                                      radius="sm"
                                      tt="none"
                                      leftSection={<PackageIcon size={14} />}
                                    >
                                      {dn.sizeCode}
                                    </Badge>
                                  </Tooltip>
                                )}
                                {dn?.totalWeightKg ? (
                                  <Tooltip label={t("delivery:route.detail.weight")} withArrow>
                                    <Group gap={2} wrap="nowrap">
                                      <ScalesIcon size={16} color="var(--mantine-color-gray-6)" />
                                      <Text size="sm" c="gray.8" fw={500}>
                                        {Math.round(dn.totalWeightKg)} {t("delivery:route.unit.kg")}
                                      </Text>
                                    </Group>
                                  </Tooltip>
                                ) : null}
                                <StopStatusBadge status={stop.status} />
                              </Group>
                            </Group>
                          </Card>
                        )}
                      </Draggable>
                    )
                  })}
                  {dropProvided.placeholder}
                </Stack>
              )}
            </Droppable>
          </DragDropContext>
        )}
      </Box>

      {/* Optimize confirmation modal */}
      <Modal
        opened={optimizeOpened}
        onClose={closeOptimize}
        title={t("delivery:route.detail.optimizeTitle")}
        size="md"
      >
        <Stack gap="md">
          <Text size="md">
            {hasWarehouseCoords
              ? t("delivery:route.detail.optimizeFromWarehouse", { name: wh?.name ?? "-" })
              : t("delivery:route.detail.optimizeFromCentre")}
          </Text>
          <Text size="sm" c="gray.7">
            {t("delivery:route.detail.optimizeStart", { point: `${origin.lat}, ${origin.lng}` })}
          </Text>
          <Alert color="gray" variant="light">
            {t("delivery:route.detail.optimizeWarn")}
          </Alert>
          <Group justify="flex-end" gap="sm">
            <Button variant="default" onClick={closeOptimize} disabled={submitting}>
              {t("common:actions.dismiss")}
            </Button>
            <Button loading={submitting} onClick={handleOptimize}>
              {t("delivery:route.detail.optimize")}
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Complete confirmation: «Дуусгах» cannot be undone (O10 / O121) */}
      <Modal
        opened={completeOpened}
        onClose={closeComplete}
        title={t("delivery:route.detail.completeTitle")}
        size="md"
      >
        <Stack gap="md">
          <Text size="md">{t("delivery:route.detail.completeText")}</Text>
          <Box>
            <Text size="sm" c="gray.7">{t("delivery:route.detail.closedStops")}</Text>
            <Text size="xl" fw={700} ff="monospace">{closedStops} / {stops.length}</Text>
          </Box>
          {closedStops < stops.length && (
            <Alert color="orange" variant="light">
              {t("delivery:route.detail.completeOpen", { count: stops.length - closedStops })}
            </Alert>
          )}
          <Alert color="gray" variant="light">
            {t("delivery:route.detail.completeIrreversible")}
          </Alert>
          <Group justify="flex-end" gap="sm">
            <Button variant="default" onClick={closeComplete} disabled={submitting}>
              {t("common:actions.dismiss")}
            </Button>
            <Button loading={submitting} onClick={handleComplete}>
              {t("delivery:route.detail.complete")}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  )
}
